import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Package, DollarSign, Clock } from "lucide-react";
import { getCaseStudyBySlug } from "@/content/case-studies";
import IllustrativeDisclaimer from "@/components/site/IllustrativeDisclaimer";
import Seo from "@/lib/Seo";

const Section = ({ overline, heading, body, index }) => (
  <motion.section
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay: index * 0.08 }}
    className="py-16 md:py-20 border-b border-black/10"
  >
    <div className="max-w-[900px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
      <div className="col-span-12 md:col-span-3">
        <div className="overline">{overline}</div>
        <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-[0.95] tracking-tight">
          {heading.split(" ").slice(0, -1).join(" ")}{" "}
          <em className="italic text-[color:var(--gold)]">
            {heading.split(" ").slice(-1)}
          </em>
        </h2>
      </div>
      <div className="col-span-12 md:col-span-9">
        <div className="prose-editorial">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
        </div>
      </div>
    </div>
  </motion.section>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const study = getCaseStudyBySlug(slug);

  if (!study) {
    return (
      <div className="pt-40 pb-32 text-center px-6">
        <div className="overline">404</div>
        <h1 className="mt-6 font-serif text-5xl">Deal structure not found.</h1>
        <button
          onClick={() => navigate("/deal-structures")}
          className="mt-8 gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]"
        >
          Back to deal structures →
        </button>
      </div>
    );
  }

  const { meta, situation, structure, outcome } = study;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: meta.title,
    description: meta.excerpt,
    image: meta.image,
    datePublished: meta.date,
    author: { "@type": "Organization", name: "Steady Path Funding" },
    publisher: {
      "@type": "Organization",
      name: "Steady Path Funding",
      logo: {
        "@type": "ImageObject",
        url: "https://www.steadypathfunding.com/favicon.svg",
      },
    },
    articleSection: "Deal structure · " + meta.industry,
  };

  const FACTS = [
    { icon: DollarSign, label: "Illustrative amount", value: meta.amount },
    { icon: Clock, label: "Illustrative timeline", value: meta.timeToFund },
    { icon: Package, label: "Product", value: meta.product },
    { icon: MapPin, label: "Scenario · Industry", value: `${meta.state} · ${meta.industry}` },
  ];

  return (
    <article>
      <Seo
        path={`/deal-structures/${meta.slug}`}
        title={meta.title}
        description={meta.excerpt}
        image={meta.image}
        type="article"
        publishedTime={meta.date}
        jsonLd={articleLd}
      />

      {/* Hero */}
      <section className="pt-36 pb-12 border-b border-black/10">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Link
            to="/deal-structures"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60 hover:text-[color:var(--gold)] transition-colors mb-10"
          >
            <ArrowLeft size={14} /> All deal structures
          </Link>
          <div className="overline">Deal structure · {meta.challenge}</div>
          <h1 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">
            {meta.title}
          </h1>
          <p className="mt-8 text-[color:var(--bone)]/75 text-lg leading-relaxed max-w-2xl">
            {meta.excerpt}
          </p>

          {/* Illustrative disclaimer — appears prominently on every detail page. */}
          <div className="mt-10">
            <IllustrativeDisclaimer />
          </div>
        </div>
      </section>

      {/* Facts strip */}
      <section className="border-b border-black/10 bg-[color:var(--bg-elev)]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div
            data-testid="case-study-facts"
            className="grid grid-cols-2 md:grid-cols-4 divide-x divide-black/10"
          >
            {FACTS.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.label} className="p-6 md:p-8 first:border-l-0">
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--gold)]">
                    <Icon size={12} /> {f.label}
                  </div>
                  <div className="mt-3 font-serif text-xl md:text-2xl text-[color:var(--bone)] leading-tight">
                    {f.value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cover */}
      <section className="border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="relative aspect-[16/9] overflow-hidden">
            <img src={meta.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/12" />
          </div>
        </div>
      </section>

      {/* Three-part narrative */}
      <Section overline="Chapter one" heading="The situation" body={situation} index={0} />
      <Section overline="Chapter two" heading="The structure" body={structure} index={1} />
      <Section overline="Chapter three" heading="The outcome" body={outcome} index={2} />

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[color:var(--surface)]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 md:col-span-8">
              <div className="overline">Different file, same discipline</div>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1]">
                Working a file like this <em className="italic text-[color:var(--gold)]">of your own</em>?
              </h2>
              <p className="mt-6 text-[color:var(--bone)]/75 leading-relaxed max-w-lg">
                A four-minute application gets you a broker call within the hour. No credit pull. No obligation.
              </p>
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right">
              <Link
                to="/apply"
                data-testid="case-study-apply-cta"
                className="inline-flex items-center gap-3 bg-[color:var(--gold)] text-black px-6 py-4 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors"
              >
                Start application <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
